import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import "./theme.css"; // Import theme CSS variables
import { loadTheme } from "./utils/themeLoader";
import { logout } from "./utils/authUtils";
import { identifyUserToCRM } from "./utils/crmWidgetUtils";
import { CartProvider } from "./contexts/CartContext";
import { ChecklistProvider } from "./contexts/ChecklistContext";
import { DemoModeProvider } from "./contexts/DemoModeContext";
import DemoModeBanner from "./components/demo/DemoModeBanner";
import DemoExitIntent from "./components/demo/DemoExitIntent";
import DemoTooltip from "./components/demo/DemoTooltip";
import ProtectedRoute from "./components/ProtectedRoute";
import HomePage from "./pages/HomePage";
// Legacy imports removed - unified professional dashboard
import FreelancerLanding from "./pages/customer/FreelancerLanding";
import FindFreelancers from "./pages/FindFreelancers";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import PublicProducts from "./pages/Products";
import CountryWaitlistPage from "./pages/CountryWaitlistPage";
import WaitlistClaimPage from "./pages/waitlist/WaitlistClaimPage";
import PackageCatalogue from "./pages/customer/PackageCatalogue";
import ForBusinesses from "./pages/ForBusinesses";
import PublicPricing from "./pages/PublicPricing";
import PublicFeatures from "./pages/PublicFeatures";
import HowItWorks from "./pages/HowItWorks";
import DemoLandingPage from "./pages/DemoLandingPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import Press from "./pages/Press";
import HelpCenter from "./pages/HelpCenter";
import FAQ from "./pages/FAQ";
import Safety from "./pages/Safety";
import JoinAsProfessional from "./pages/JoinAsProfessional";
import ShortUrlRedirect from "./pages/ShortUrlRedirect"; // Short URL redirect handler (e.g., /map/abc123)
import SlotConflictPage from "./pages/customer/SlotConflictPage"; // Slot conflict: pick a new time (Part B)
import WalkInConfirmationPage from "./pages/customer/WalkInConfirmationPage"; // Walk-in: Stripe success redirect
import WalkInCancelledPage from "./pages/customer/WalkInCancelledPage"; // Walk-in: Stripe cancel redirect
import CompleteOwnerRegistration from "./components/CompleteOwnerRegistration";
import SignupSelection from "./pages/auth/SignupSelection";
import ShopRegistrationPage from "./pages/ShopRegistrationPage";
import FreelancerSignup from "./pages/FreelancerSignup";
import BoothRentalSignup from "./pages/BoothRentalSignup";
import BoothRenterDashboard from "./pages/BoothRenterDashboard";
import FreelancerAccountForm from "./components/FreelancerAccountForm";
import FreelancerVerifyEmail from "./pages/FreelancerVerifyEmail";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import RegistrationSuccess from "./pages/RegistrationSuccess";

// Shop Dashboard and sub-pages
import ShopDashboardPage from "./pages/shops/ShopDashboardPage";
import Bookings from "./pages/shops/Bookings";
import Services from "./pages/shops/Services";
import ProductsV2 from "./pages/shops/ProductsV2"; // New two-level product system
import Settings from "./pages/shops/Settings";
import BusinessProfile from "./pages/shops/BusinessProfile";
import AdminProfile from "./pages/admin/AdminProfile";
import BookingSettings from "./pages/shops/BookingSettings";
import NotificationSettings from "./pages/shops/NotificationSettings";
import ShopNotifications from "./pages/shops/ShopNotifications";
import PackageSettings from "./pages/shops/PackageSettings";
import BillingPayouts from "./pages/shops/BillingPayouts";
import SecuritySettings from "./pages/shops/SecuritySettings";
import Locations from "./pages/shops/Locations";
import LocationTimezone from "./pages/shops/LocationTimezone";
import Analytics from "./pages/shops/Analytics";
import OwnerPayoutHistory from "./pages/shops/OwnerPayoutHistory";
import ProductAnalytics from "./pages/shops/ProductAnalytics";
import ShopStaffManagement from "./pages/shops/Staff"; // Shop's staff management page
import ShopManagers from "./pages/shops/Managers"; // Shop managers management page
import ShopStaffProfile from "./pages/shops/StaffProfile"; // Shop's view of a staff member
import BoothRentalManagement from "./pages/shops/BoothRentalManagement"; // Booth rental management (R7)
import TimeOffRequests from "./pages/shops/TimeOffRequests"; // Time-off approval dashboard
import Contacts from "./pages/shops/Contacts"; // Contacts management page
import LocationProfile from "./pages/shops/LocationProfile";
import VerifyShopPage from "./pages/shops/VerifyShopPage";
import VerifyCodePage from "./components/VerifyCodePage";
import ShopPackages from "./pages/shops/Packages";
import ShopWaitlist from "./pages/shops/Waitlist";
import BusinessLooksPage from "./pages/shops/BusinessLooksPage";
import BusinessReviews from "./pages/shops/BusinessReviews";
import ProfessionalProfileSettings from "./pages/shops/ProfessionalProfileSettings";
import BusinessSubscription from "./pages/business/BusinessSubscription";
import BusinessBilling from "./pages/business/BusinessBilling";
import BookingImport from "./pages/shops/BookingImport";
import CustomerImport from "./pages/shops/CustomerImport";
import MultiLocationRequests from "./pages/shops/MultiLocationRequests";
import BusinessOperations from "./pages/shops/BusinessOperations";

// OAuth callback pages
import OAuthSuccess from "./pages/auth/OAuthSuccess";
import OAuthError from "./pages/auth/OAuthError";

// Professional section pages - Unified dashboard for all professional types
import StaffDashboard from "./pages/staff/StaffDashboard";
import StaffProfile from "./pages/staff/StaffProfile";
import StaffReviews from "./pages/staff/StaffReviews";
import StaffProfileEdit from "./pages/staff/StaffProfileEdit";
import StaffServices from "./pages/staff/StaffServices";
import StaffServiceAdd from "./pages/staff/StaffServiceAdd";
import StaffServiceBulkAdd from "./pages/staff/StaffServiceBulkAdd";
import StaffServiceEdit from "./pages/staff/StaffServiceEdit";
import UnifiedScheduleCalendar from "./pages/staff/UnifiedScheduleCalendar";
import StaffBookings from "./pages/staff/StaffBookings";
import ReferralAgentDashboard from "./pages/referral/ReferralAgentDashboard";
import AgentLayout from "./pages/referral/AgentLayout";
import AgentOverview from "./pages/referral/AgentOverview";
import AgentReferrals from "./pages/referral/AgentReferrals";
import AgentEarnings from "./pages/referral/AgentEarnings";
import AgentPayouts from "./pages/referral/AgentPayouts";
import AgentLinks from "./pages/referral/AgentLinks";
import AgentProfile from "./pages/referral/AgentProfile";
import JoinReferralPage from "./pages/referral/JoinReferralPage";
import StaffBookingImport from "./pages/staff/StaffBookingImport";
import BookingDetail from "./pages/staff/BookingDetail";
import FreelancerBookingDetail from "./pages/freelancer/FreelancerBookingDetail";
import FreelancerBookingImport from "./pages/freelancer/FreelancerBookingImport";
import StaffNotifications from "./pages/staff/StaffNotifications";
import StaffSettings from "./pages/staff/StaffSettings";
import StaffEarningsPerformance from "./pages/staff/StaffEarningsPerformance";
import StaffPayouts from "./pages/staff/StaffPayouts";
import WaitlistManagement from "./pages/staff/WaitlistManagement";
import PackageSubscriptions from "./pages/staff/PackageSubscriptions";
import MultiLocationRequest from "./pages/staff/MultiLocationRequest";
import BusinessOrders from "./pages/shops/BusinessOrders";
import FreelancerOrders from "./pages/freelancer/FreelancerOrders";
import ServiceRequestsManagement from "./pages/shops/ServiceRequestsManagement";

// Freelancer section pages - Independent professional dashboard
import FreelancerDashboard from "./pages/freelancer/FreelancerDashboard";
import FreelancerProfile from "./pages/freelancer/FreelancerProfile";
import FreelancerReviews from "./pages/freelancer/FreelancerReviews";
import FreelancerProfileEdit from "./pages/freelancer/FreelancerProfileEdit";
import FreelancerServices from "./pages/freelancer/FreelancerServices";
import FreelancerServiceAdd from "./pages/freelancer/FreelancerServiceAdd";
import FreelancerServiceEdit from "./pages/freelancer/FreelancerServiceEdit";
import FreelancerServiceBulkAdd from "./pages/freelancer/FreelancerServiceBulkAdd";
import FreelancerSchedule from "./pages/freelancer/FreelancerSchedule";
import FreelancerBookings from "./pages/freelancer/FreelancerBookings";
import FreelancerProducts from "./pages/freelancer/FreelancerProducts";
import FreelancerProductAdd from "./pages/freelancer/FreelancerProductAdd";
import FreelancerProductEdit from "./pages/freelancer/FreelancerProductEdit";
import FreelancerAnalytics from "./pages/freelancer/FreelancerAnalytics";
import FreelancerEarnings from "./pages/freelancer/FreelancerEarnings";
import FreelancerNotifications from "./pages/freelancer/FreelancerNotifications";
import FreelancerSettings from "./pages/freelancer/FreelancerSettings";
import FreelancerSubscription from "./pages/freelancer/FreelancerSubscription";
import FreelancerBilling from "./pages/freelancer/FreelancerBilling";
import FreelancerPaymentMethods from "./pages/freelancer/FreelancerPaymentMethods";
import StorefrontSetupPage from "./pages/storefront/StorefrontSetupPage";

// Subscription & Pricing
import PricingPage from "./pages/subscription/PricingPage";
import PaymentMethodSetup from "./components/subscription/PaymentMethodSetup";
import CheckoutPreview from "./pages/subscription/CheckoutPreview";
import SubscriptionWidget from "./components/subscription/SubscriptionWidget";

// Migration Wizard
import MigrationWizard from "./pages/migration/MigrationWizard";

// Admin Dashboard
import PortalDashboard from "./pages/admin/PortalDashboard";
import PlatformSettings from "./pages/admin/PlatformSettings";
import ThemeSettings from "./pages/admin/ThemeSettings";
import EmailSenderSettings from "./pages/admin/EmailSenderSettings";
import BusinessManagement from "./pages/admin/BusinessManagement";
import BusinessDetail from "./pages/admin/BusinessDetail";
import ProfessionalTypesManagement from "./pages/admin/ProfessionalTypesManagement";
import BusinessTypesManagement from "./pages/admin/BusinessTypesManagement";
import ProductCategoriesManagement from "./pages/admin/ProductCategoriesManagement";
import ServiceCategoriesManagement from "./pages/admin/ServiceCategoriesManagement";
import ServiceTemplatesManagement from "./pages/admin/ServiceTemplatesManagement";
import FraudDashboard from "./pages/admin/FraudDashboard";
import PromoCodeManagement from "./pages/admin/PromoCodeManagement";
import RevenueAnalytics from "./pages/admin/RevenueAnalytics";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import TransferJobs from "./pages/admin/TransferJobs";
import PayoutWeekdayDistribution from "./pages/admin/PayoutWeekdayDistribution";
import RefundGovernance from "./pages/admin/RefundGovernance";
import ProductReturnsOpsConsole from "./pages/admin/ProductReturnsOpsConsole";
import PackageFreezeReviews from "./pages/admin/PackageFreezeReviews";
import ProductTaxAudit from "./pages/admin/ProductTaxAudit";
import ShopTaxRates from "./pages/admin/ShopTaxRates";
import ProductTransactions from "./pages/admin/ProductTransactions";
import UserManagement from "./pages/admin/UserManagement";
import SupportAgentsManagement from "./pages/admin/SupportAgentsManagement";
import ReferralAgentsManagement from "./pages/admin/ReferralAgentsManagement";
import SupportMigration from "./pages/admin/SupportMigration";
import LeadsManagement from "./pages/admin/LeadsManagement";
import CountryWaitlistManagement from "./pages/admin/CountryWaitlistManagement";
import MarketingDashboard from "./pages/admin/MarketingDashboard";
import LooksModeration from "./pages/admin/LooksModeration";
import SMSOptOutManagement from "./pages/admin/SMSOptOutManagement";
import DemoDataReset from "./pages/admin/DemoDataReset";
import BusinessMarketingPage from "./pages/marketing/BusinessMarketingPage";
import FreelancerMarketingPage from "./pages/marketing/FreelancerMarketingPage";
import CampaignDetailsPage from "./pages/marketing/CampaignDetailsPage";
import BusinessSMSCreditsPage from "./pages/marketing/BusinessSMSCreditsPage";
import FreelancerSMSCreditsPage from "./pages/marketing/FreelancerSMSCreditsPage";
import ShopManagement from "./pages/admin/ShopManagement";
import CompleteRegistrationPage from "./pages/auth/CompleteRegistrationPage";
import CompletePhonePage from "./pages/auth/CompletePhonePage";

// Authentication
import LoginPage from "./pages/auth/LoginPage";
import CustomerSignupPage from "./pages/auth/CustomerSignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import MultiRoleLanding from "./pages/auth/MultiRoleLanding";

// Customer Booking
import CustomerBooking from "./pages/CustomerBooking";

// Customer Pages
import ShopListings from "./pages/customer/ShopListings";
import SearchResults from "./pages/customer/SearchResults";
import ShopLanding from "./pages/customer/ShopLanding";
import BusinessLocationsPage from "./pages/customer/BusinessLocationsPage";
import ShopCatalogue from "./pages/customer/ShopCatalogue";
import CartPage from "./pages/customer/CartPage";
import CheckoutPage from "./pages/customer/CheckoutPage";
import OrderSuccess from "./pages/customer/OrderSuccess";
import AdvancedSearch from "./pages/customer/AdvancedSearch";
import TrackOrder from "./pages/customer/TrackOrder"; // Phase E2
import CustomerDashboard from "./pages/customer/CustomerDashboard"; // Phase E4
import ServicesList from "./pages/customer/ServicesList";
import ShopPackagesPage from "./pages/customer/ShopPackages";
import ProfessionalSelection from "./pages/customer/ProfessionalSelection";
import ProfessionalProfile from "./pages/customer/ProfessionalProfile";
import FreelancerServicesListing from "./pages/customer/FreelancerServicesListing";
import FreelancerBooking from "./pages/FreelancerBooking";
import CustomerProfile from "./pages/customer/CustomerProfile";
import BookingConfirmation from "./pages/customer/BookingConfirmation";
import PackagePurchaseConfirmation from "./pages/customer/PackagePurchaseConfirmation";
import PackageTerms from "./pages/customer/PackageTerms";
import PackagePurchasePage from "./pages/customer/PackagePurchasePage";
import PackageTransferPage from "./pages/guest/PackageTransferPage";
import PackageLookupPage from "./pages/guest/PackageLookupPage";
import PackageFreezePage from "./pages/guest/PackageFreezePage";
// MAIN-ONLY PAGE. It has no history on demo, so a demo-sourced App.js never mentions it. The
// route below is what keeps it reachable on production; without both lines the page ships dead.
import PublicDemoBookingPage from "./pages/PublicDemoBookingPage";
import BookingCancelled from "./pages/customer/BookingCancelled";
import BookingReschedulePage from "./pages/customer/BookingReschedulePage";
import ProductDetail from "./pages/ProductDetail";
import ReviewForm from "./pages/customer/ReviewForm"; // Phase G
import ProductReviewForm from "./pages/customer/ProductReviewForm";
import GuestRefundRequest from "./pages/guest/GuestRefundRequest";
import ConsentResponsePage from "./pages/looks/ConsentResponsePage";
import LooksFeedPage from "./pages/looks/LooksFeedPage";
import LookDetailPage from "./pages/looks/LookDetailPage";
import AllLooksPage from "./pages/looks/AllLooksPage";
import CollectionsPage from "./pages/looks/CollectionsPage";
import NotificationsPage from "./pages/looks/NotificationsPage";
import MyLooksPage from "./pages/staff/MyLooksPage";
import ServiceInventoryPage from "./pages/staff/ServiceInventoryPage";

// Utility Components
import ScrollToTop from "./components/ScrollToTop";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const SESSION_ACTIVITY_KEY = "ogestyle_last_activity_at";
const SESSION_TIMEOUT_MINUTES_DEFAULT = 120;

function normalizeSessionTimeoutMinutes(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return SESSION_TIMEOUT_MINUTES_DEFAULT;
  if (parsed <= 0) return SESSION_TIMEOUT_MINUTES_DEFAULT;
  return Math.floor(parsed);
}

function SessionTimeoutGuard({ children }) {
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(
    SESSION_TIMEOUT_MINUTES_DEFAULT,
  );
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const lastSeenTokenRef = useRef(localStorage.getItem("ogestyle_token"));

  useEffect(() => {
    let cancelled = false;

    const loadSessionTimeout = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/settings/public`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const timeout =
          data?.settings?.securitySettings?.sessionTimeoutMinutes ??
          SESSION_TIMEOUT_MINUTES_DEFAULT;
        setSessionTimeoutMinutes(normalizeSessionTimeoutMinutes(timeout));
      } catch (_err) {
        // Keep default timeout on network error.
      }
    };

    loadSessionTimeout();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timeoutMs = sessionTimeoutMinutes * 60 * 1000;

    const syncTokenTransition = () => {
      const currentToken = localStorage.getItem("ogestyle_token");

      // Fresh login in the same SPA session: reset inactivity timer so user
      // is not immediately logged out again based on the previous session's timestamp.
      if (currentToken && currentToken !== lastSeenTokenRef.current) {
        localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now()));
        setShowSessionExpiredModal(false);
      }

      if (!currentToken && lastSeenTokenRef.current) {
        localStorage.removeItem(SESSION_ACTIVITY_KEY);
      }

      lastSeenTokenRef.current = currentToken;
    };

    const touchActivity = () => {
      if (!localStorage.getItem("ogestyle_token")) return;

      const now = Date.now();
      const last = Number(localStorage.getItem(SESSION_ACTIVITY_KEY) || 0);

      // Throttle writes to avoid excessive localStorage churn.
      if (!last || now - last > 30 * 1000) {
        localStorage.setItem(SESSION_ACTIVITY_KEY, String(now));
      }
    };

    const ensureActivitySeeded = () => {
      if (!localStorage.getItem("ogestyle_token")) return;
      if (!localStorage.getItem(SESSION_ACTIVITY_KEY)) {
        localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now()));
      }
    };

    const checkTimeout = () => {
      syncTokenTransition();

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        return;
      }

      ensureActivitySeeded();
      const lastActivityAt = Number(localStorage.getItem(SESSION_ACTIVITY_KEY) || 0);
      if (!lastActivityAt) return;

      const inactiveMs = Date.now() - lastActivityAt;
      if (inactiveMs >= timeoutMs) {
        setShowSessionExpiredModal(true);
        logout();
        return;
      }
    };

    const trackedEvents = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "focus",
    ];

    ensureActivitySeeded();
    trackedEvents.forEach((eventName) =>
      window.addEventListener(eventName, touchActivity, { passive: true }),
    );

    const handleStorage = (event) => {
      if (event.key !== "ogestyle_token") return;
      syncTokenTransition();
    };

    window.addEventListener("storage", handleStorage);

    syncTokenTransition();
    const intervalId = window.setInterval(checkTimeout, 30 * 1000);
    checkTimeout();

    return () => {
      trackedEvents.forEach((eventName) =>
        window.removeEventListener(eventName, touchActivity),
      );
      window.removeEventListener("storage", handleStorage);
      window.clearInterval(intervalId);
    };
  }, [sessionTimeoutMinutes]);

  return (
    <>
      {children}
      {showSessionExpiredModal ? (
        <div
          className="session-timeout-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-timeout-title"
        >
          <div className="session-timeout-modal-content">
            <h3 id="session-timeout-title" className="session-timeout-modal-title">
              Session Ended
            </h3>
            <p className="session-timeout-modal-message">
              You were logged out due to inactivity. Please log in again to continue.
            </p>
            <div className="session-timeout-modal-actions">
              <button
                className="session-timeout-modal-button"
                type="button"
                onClick={() => setShowSessionExpiredModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function PhoneCompletionGate({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [authVersion, setAuthVersion] = useState(0);

  useEffect(() => {
    const bumpAuthVersion = () => setAuthVersion((current) => current + 1);
    const handleStorage = (event) => {
      if (event.key === "ogestyle_token") {
        bumpAuthVersion();
      }
    };

    window.addEventListener("authStateChanged", bumpAuthVersion);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("authStateChanged", bumpAuthVersion);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      return;
    }

    const shouldEnforcePhoneForPath = (pathname = "") => {
      const transactionPrefixes = [
        "/checkout",
        "/cart",
        "/book",
        "/booking",
        "/product/",
        "/shop/",
      ];

      // Seller/admin dashboards should never be hard-blocked by phone completion.
      if (pathname.startsWith("/dashboard")) {
        return false;
      }

      return transactionPrefixes.some(
        (prefix) => pathname === prefix || pathname.startsWith(prefix),
      );
    };

    const exemptPaths = [
      "/login",
      "/verify-code",
      "/forgot-password",
      "/verify-email",
      "/complete-profile",
      "/auth/success",
      "/auth/error",
      "/registration-success",
      "/complete-registration",
    ];

    const isExemptPath = exemptPaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`),
    );

    if (isExemptPath || !shouldEnforcePhoneForPath(location.pathname)) {
      return;
    }

    let cancelled = false;

    const enforcePhoneCompletion = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          return;
        }

        const profile = await response.json();
        if (cancelled) {
          return;
        }

        const phone = String(profile?.phone || "").trim();
        if (!phone) {
          const redirectTarget = `${location.pathname}${location.search}`;
          navigate(
            `/complete-profile?redirect=${encodeURIComponent(redirectTarget)}`,
            { replace: true },
          );
        }
      } catch (error) {}
    };

    enforcePhoneCompletion();

    return () => {
      cancelled = true;
    };
  }, [authVersion, location.pathname, location.search, navigate]);

  return children;
}

// Country Check Component - runs on every route change
function CountryCheck({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip check if already on waitlist page
    if (location.pathname === "/country-not-available") {
      return;
    }

    // Check user's country
    const checkCountry = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/geo/check`);
        const data = await response.json();

        if (!data.isSupported) {
          navigate(`/country-not-available?country=${data.country}`);
        }
      } catch (error) {
        // Fail open - allow access if check fails (better UX)
      }
    };

    checkCountry();
  }, [navigate, location.pathname]);

  return children;
}

function App() {
  const normalizedApiBaseUrl = (API_BASE_URL || "").toLowerCase();
  const isDemoEnvironment =
    !normalizedApiBaseUrl ||
    normalizedApiBaseUrl.includes("api-demo") ||
    normalizedApiBaseUrl.includes("localhost") ||
    normalizedApiBaseUrl.includes("127.0.0.1");

  // Load theme settings from backend on app startup
  useEffect(() => {
    loadTheme();
  }, []);

  // Initialize CRM widget with user identity when authenticated
  useEffect(() => {
    // MAIN-ONLY kill switch: flipping this flag turns the CRM widget off without a deploy.
    if (window.OGESTYLE_HELPDESK_ENABLED === false) {
      return;
    }

    // Wait for widget to load (it's async)
    const initWidgetTimer = setTimeout(() => {
      const token = localStorage.getItem("ogestyle_token");
      if (token) {
        identifyUserToCRM();
      }
    }, 1000); // Give widget 1 second to load

    // Also listen for storage events (e.g., login in another tab)
    const handleStorageChange = (e) => {
      if (e.key === "ogestyle_token") {
        if (e.newValue) {
          // Token added/changed - identify user
          identifyUserToCRM();
        }
        // Token removal (logout) is handled in authUtils.js logout()
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearTimeout(initWidgetTimer);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    <HelmetProvider>
      <DemoModeProvider>
        <CartProvider>
          <ChecklistProvider>
            <Router>
              <DemoModeBanner />
              <DemoExitIntent />
              <ScrollToTop />
              <CountryCheck>
                <SessionTimeoutGuard>
                  <PhoneCompletionGate>
                    <DemoTooltip />
                    <Routes>
                    {/* Public Pages */}
                    <Route path="/" element={<HomePage />} />
                    {/* Short URL Redirect - Maps, directions, etc. */}
                    <Route path="/map/:code" element={<ShortUrlRedirect />} />
                    {/* Slot conflict: customer picks a new time after their paid slot was taken.
                        No charge - the conflict was not their fault. */}
                    <Route
                      path="/booking/slot-conflict"
                      element={<SlotConflictPage />}
                    />
                    {/* Walk-in card payment: the pages Stripe redirects the customer to.
                        Public on purpose - the walk-in customer is usually a guest with
                        no session, and `/walk-in/...` is two segments so the
                        single-segment short-link resolver at "/:code" cannot shadow it.
                        Without these routes a customer who had just PAID landed on a
                        blank page: the success/cancel URLs were set from the start but
                        nothing was ever registered for them. */}
                    <Route
                      path="/walk-in/confirmation"
                      element={<WalkInConfirmationPage />}
                    />
                    <Route
                      path="/walk-in/cancelled"
                      element={<WalkInCancelledPage />}
                    />
                    {/* Waitlist claim link from the "A slot just opened up" email.
                        utils/waitlistClaimToken.js builds /waitlist/claim?token=..., which
                        the email service renders as the "Move My Appointment" button.
                        Public on purpose: the link arrives by email, so the customer is
                        usually signed out. The page fetches its own preview and drives
                        the claim itself, and renders its own header and footer.
                        Registered before /:code so the single-segment short-link
                        resolver can never shadow it. Without this route the link was a
                        blank page - the page component existed but nothing imported it. */}
                    <Route
                      path="/waitlist/claim"
                      element={<WaitlistClaimPage />}
                    />
                    {/* Public short links (https://ogestyle.com/<code>). Registered AFTER every
                        specific route so it can never shadow them: only a single unmatched
                        path segment reaches the short-link resolver. */}
                    <Route path="/:code" element={<ShortUrlRedirect />} />
                    <Route
                      path="/country-not-available"
                      element={<CountryWaitlistPage />}
                    />
                    <Route path="/freelancers" element={<FindFreelancers />} />
                    <Route
                      path="/find-freelancers"
                      element={<Navigate to="/freelancers" replace />}
                    />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsConditions />} />
                    <Route
                      path="/terms-conditions"
                      element={<TermsConditions />}
                    />
                    <Route path="/products" element={<PublicProducts />} />
                    <Route path="/for-businesses" element={<ForBusinesses />} />
                    <Route path="/plans" element={<PublicPricing />} />
                    <Route path="/features" element={<PublicFeatures />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/demo-landing" element={<DemoLandingPage />} />
                    {/* MAIN-ONLY: the book-a-demo booking page. `/demo-landing` above is a different
                        page on demo; this one exists only on main and must stay routed here. */}
                    <Route path="/demo" element={<PublicDemoBookingPage />} />
                    {/* Production-facing landing (same page, non-demo name). Kept
                        off the "/" homepage; demo links inside stay as-is. */}
                    <Route path="/why-ogestyle" element={<DemoLandingPage />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/press" element={<Press />} />
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/safety" element={<Safety />} />
                    <Route
                      path="/for-professionals"
                      element={<JoinAsProfessional />}
                    />
                    <Route
                      path="/join-as-professional"
                      element={<Navigate to="/for-professionals" replace />}
                    />
                    <Route path="/freelancer" element={<FreelancerLanding />} />
                    <Route
                      path="/business-owner"
                      element={<Navigate to="/for-businesses" replace />}
                    />
                    <Route path="/signup" element={<SignupSelection />} />
                  {/* A referral share link: /join/<code> captures the code, then sends them to sign up. */}
                  <Route path="/join/:code" element={<JoinReferralPage />} />
                    <Route
                      path="/register-business"
                      element={<CompleteOwnerRegistration />}
                    />
                    {/* Legacy maestro/hairstylist routes removed - all professionals use /dashboard/professional */}
                    <Route
                      path="/register-shop"
                      element={<ShopRegistrationPage />}
                    />
                    <Route
                      path="/shop-registration"
                      element={<ShopRegistrationPage />}
                    />
                    <Route
                      path="/registration-success"
                      element={<RegistrationSuccess />}
                    />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route
                      path="/freelancer/signup"
                      element={<FreelancerAccountForm />}
                    />
                    <Route
                      path="/freelancer-signup"
                      element={<FreelancerAccountForm />}
                    />
                    <Route
                      path="/booth-rental/signup/:referralCode"
                      element={<BoothRentalSignup />}
                    />
                    <Route
                      path="/freelancer/verify-email"
                      element={<FreelancerVerifyEmail />}
                    />
                    <Route
                      path="/freelancer/profile"
                      element={<FreelancerSignup />}
                    />
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                      path="/complete-profile"
                      element={
                        <ProtectedRoute>
                          <CompletePhonePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer-signup"
                      element={<CustomerSignupPage />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPasswordPage />}
                    />
                    <Route path="/verify-shop" element={<VerifyShopPage />} />
                    <Route
                      path="/shop-profile"
                      element={<ShopRegistrationPage />}
                    />
                    {/* Customer Pages - New Journey Flow */}
                    <Route path="/shops" element={<ShopListings />} />
                    <Route path="/search" element={<SearchResults />} />
                    <Route path="/packages" element={<PackageCatalogue />} />
                    {/* CHECK MY PACKAGE (user, 2026-10-19). PUBLIC, and it must be: a guest bought without
                        an account, so a protected page would be closed to exactly the person who needs it.
                        READ ONLY — it takes the same three factors the booking page takes and then just
                        reports what is owned. Spending, transferring and paying stay on their own screens. */}
                    <Route path="/packages/lookup" element={<PackageLookupPage />} />
                    {/* Phase 15: buying a package is a PAGE, not a modal — linkable, refreshable, and
                        able to survive the trip to Stripe and back. PUBLIC on purpose: a guest buys a
                        package without an account, so this route must NOT be wrapped in
                        ProtectedRoute. */}
                    <Route
                      path="/packages/:packageId/purchase"
                      element={<PackagePurchasePage />}
                    />
                    {/* Stripe package-purchase return URL. This route is the ONLY trigger for\n                        activating a package purchase and emailing the buyer, so it must exist and\n                        must stay public - a guest purchase has no account to sign in with. */}
                    <Route
                      path="/packages/purchase-confirmation"
                      element={<PackagePurchaseConfirmation />}
                    />
                    {/* THE PACKAGE TERMS (user, 2026-10-14). PUBLIC on purpose: a guest buys a package
                        without an account, and a terms page nobody can open is the same as no terms page.
                        The purchase page links here and records the version the buyer read. */}
                    <Route path="/packages/terms" element={<PackageTerms />} />
                    {/* GIVING A PACKAGE AWAY (user, 2026-10-09). PUBLIC, and it has to be: the person most
                        likely to give a package away is a GUEST, who bought without an account and could not
                        open a protected page even if they wanted to. The page asks for the three factors
                        instead of for a login, and the LINK ITSELF carries no authority - see the page. */}
                    <Route
                      path="/packages/:purchaseId/transfer"
                      element={<PackageTransferPage />}
                    />
                    {/* STOPPING A PACKAGE THE SENDER DID NOT MEAN TO GIVE AWAY (user, 2026-10-09).
                        Public for the same reason the transfer is: the person who needs it is usually a
                        guest, and they need it fast. The factors asked for are the ones they held BEFORE
                        the transfer — the retired number is their recovery credential. */}
                    <Route
                      path="/packages/:purchaseId/freeze"
                      element={<PackageFreezePage />}
                    />
                    {/* Global catalogue now aliases to functional products listing */}
                    <Route path="/catalogue" element={<PublicProducts />} />
                    {/* Shopping Cart */}
                    <Route path="/cart" element={<CartPage />} />
                    {/* Checkout */}
                    <Route path="/checkout" element={<CheckoutPage />} />
                    {/* Order Success */}
                    <Route path="/order/success" element={<OrderSuccess />} />
                    {/* Phase E2: Order Tracking */}
                    <Route path="/track-order" element={<TrackOrder />} />
                    <Route
                      path="/looks/consent"
                      element={<ConsentResponsePage />}
                    />
                    <Route path="/looks/feed" element={<LooksFeedPage />} />
                    <Route path="/looks/all" element={<AllLooksPage />} />
                    <Route path="/looks/collections" element={<CollectionsPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/look/:lookId" element={<LookDetailPage />} />
                    {/* Phase E4: Customer Dashboard */}
                    <Route
                      path="/customer/dashboard"
                      element={
                        <ProtectedRoute>
                          <CustomerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer/dashboard/appointments"
                      element={
                        <Navigate
                          to="/customer/dashboard?tab=bookings"
                          replace
                        />
                      }
                    />
                    {/* Business Locations Landing Page - Email Marketing */}
                    <Route
                      path="/business/:businessSlug"
                      element={<BusinessLocationsPage />}
                    />
                    {/* Shop Landing Page */}
                    <Route
                      path="/business/:businessSlug/:locationSlug"
                      element={<ShopLanding />}
                    />
                    {/* Shop Catalogue Page - Products & Packages */}
                    <Route
                      path="/business/:businessSlug/:locationSlug/catalogue"
                      element={<ShopCatalogue />}
                    />
                    {/* Services List */}
                    <Route
                      path="/business/:businessSlug/:locationSlug/services"
                      element={<ServicesList />}
                    />
                    {/* Shop Packages — every package this shop sells */}
                    <Route
                      path="/business/:businessSlug/:locationSlug/packages"
                      element={<ShopPackagesPage />}
                    />
                    {/* Professional Selection */}
                    <Route
                      path="/business/:businessSlug/:locationSlug/book"
                      element={<ProfessionalSelection />}
                    />
                    {/* Individual Professional Profile with business context */}
                    <Route
                      path="/business/:businessSlug/:locationSlug/professional/:slug"
                      element={<ProfessionalProfile />}
                    />
                    {/* Legacy Professional/Freelancer Profile routes (slug-based, shareable) */}
                    <Route
                      path="/professional/:slug"
                      element={<ProfessionalProfile />}
                    />
                    <Route
                      path="/freelancer/:slug"
                      element={<ProfessionalProfile />}
                    />
                    <Route
                      path="/freelancers/:slug"
                      element={<ProfessionalProfile />}
                    />
                    {/* Freelancer Services Listing (Phase C11.B) */}
                    <Route
                      path="/freelancer/:slug/services"
                      element={<FreelancerServicesListing />}
                    />
                    {/* Freelancer Booking (Phase C11.B) */}
                    <Route
                      path="/freelancer-booking"
                      element={<FreelancerBooking />}
                    />
                    {/* OAuth callback routes */}
                    <Route path="/auth/success" element={<OAuthSuccess />} />
                    <Route path="/auth/error" element={<OAuthError />} />
                    {/* Customer pages */}
                    <Route
                      path="/my-appointments"
                      element={
                        <Navigate
                          to="/customer/dashboard?tab=bookings"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/my-profile"
                      element={
                        <ProtectedRoute>
                          <CustomerProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/review" element={<ReviewForm />} />{" "}
                    {/* Phase G */}
                    <Route
                      path="/product-review"
                      element={<ProductReviewForm />}
                    />
                    <Route
                      path="/refund-service"
                      element={<GuestRefundRequest />}
                    />
                    <Route path="/search" element={<AdvancedSearch />} />
                    <Route path="/booking" element={<CustomerBooking />} />
                    <Route path="/booking/reschedule" element={<BookingReschedulePage />} />
                    <Route
                      path="/booking-confirmation"
                      element={<BookingConfirmation />}
                    />
                    <Route
                      path="/booking-cancelled"
                      element={<BookingCancelled />}
                    />
                    <Route
                      path="/product/:productId"
                      element={<ProductDetail />}
                    />
                    <Route
                      path="/shop/:storefrontHandle/:locationIdentifier/products"
                      element={<ShopCatalogue />}
                    />
                    <Route
                      path="/shop/:shopIdentifier/products"
                      element={<ShopCatalogue />}
                    />
                    <Route
                      path="/freelancer/:staffId/products"
                      element={<FreelancerServicesListing />}
                    />
                    {/* Shop Dashboard */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Migration Wizard - Business */}
                    <Route
                      path="/dashboard/migration/:businessId"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <MigrationWizard type="business" />
                        </ProtectedRoute>
                      }
                    />
                    {/* Migration Wizard - Freelancer */}
                    <Route
                      path="/dashboard/freelancer/migration/:staffId"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <MigrationWizard type="freelancer" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/bookings"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <Bookings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/referrals"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R4", "R9"]}>
                          <ReferralAgentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    {/*
                      THE AGENT AREA (2026-11-01). An R9 has no shop and no staff profile, so this is a
                      self-contained shell with its own navigation — overview, referrals, earnings, payouts,
                      share links and profile. Payouts is the page that lets them register a bank account,
                      without which commission could never be paid.
                    */}
                    <Route
                      path="/dashboard/agent"
                      element={
                        <ProtectedRoute allowedRoles={["R9"]}>
                          <AgentLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AgentOverview />} />
                      <Route path="referrals" element={<AgentReferrals />} />
                      <Route path="earnings" element={<AgentEarnings />} />
                      <Route path="payouts" element={<AgentPayouts />} />
                      <Route path="links" element={<AgentLinks />} />
                      <Route path="profile" element={<AgentProfile />} />
                    </Route>
                    <Route
                      path="/dashboard/bookings/import"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BookingImport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/services"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <Services />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/products"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ProductsV2 />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/products/storefront-setup"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <StorefrontSetupPage roleType="R1" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/orders"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <BusinessOrders />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/analytics"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <Analytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/payouts"
                      element={
                        <ProtectedRoute
                          allowedRoles={["R1"]}
                          requireActiveRoleMatch={true}
                        >
                          <OwnerPayoutHistory />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/inventory"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ServiceInventoryPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Reports is its own page and its own URL, not a tab: it answers a
                        different question (what did this cost, what has happened) on a
                        different rhythm (monthly, not daily). R2 is allowed here and sees the
                        history; the cost report is refused to them by the SERVER, and the page
                        says so rather than showing a gap. */}
                    <Route
                      path="/dashboard/inventory/reports"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ServiceInventoryPage view="reports" />
                        </ProtectedRoute>
                      }
                    />
                    {/* R3's own view. A professional delivers the service and owns no
                        stock, so their page is the record of what has been handed to them
                        — the other half of "every movement names a counterparty". Being
                        named against stock has to be checkable by the person named. */}
                    <Route
                      path="/dashboard/professional/inventory"
                      element={
                        <ProtectedRoute allowedRoles={["R3"]}>
                          <ServiceInventoryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/marketing"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BusinessMarketingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/marketing/campaign/:campaignId"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <CampaignDetailsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/marketing/sms-credits"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BusinessSMSCreditsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/product-analytics"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <ProductAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    {/* R2 may VIEW packages; they cannot create, edit or delete one. The page hides its
                        owner-only controls for R2 and the API enforces the rest with authorizeRoles("R1"),
                        so allowing R2 here grants no capability. It previously allowed R1 only, which
                        silently redirected an R2 to /dashboard - the page looked broken rather than
                        forbidden. */}
                    <Route
                      path="/dashboard/packages"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopPackages />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/reviews"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <BusinessReviews />
                        </ProtectedRoute>
                      }
                    />
                    {/* The owner's notification list. It did not exist: the bell and the menu entry were only
                        on the professional and freelancer sidebars, so an owner had no way to read the alerts
                        written to their own account. */}
                    <Route
                      path="/dashboard/notifications"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopNotifications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/waitlist"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopWaitlist />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/looks"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <BusinessLooksPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/operations"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BusinessOperations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/profile"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BusinessProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/booking"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BookingSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/packages"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <PackageSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/notifications"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <NotificationSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/billing"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BillingPayouts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/subscription"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BusinessSubscription />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/invoices"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BusinessBilling />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/subscription/payment"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <PaymentMethodSetup />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/subscription/checkout"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <CheckoutPreview />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/security"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <SecuritySettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings/professional-profile"
                      element={
                        <ProtectedRoute
                          allowedRoles={["R1"]}
                          requireActiveRoleMatch={true}
                        >
                          <ProfessionalProfileSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/admin/profile"
                      element={
                        <ProtectedRoute allowedRoles={["R2"]}>
                          <AdminProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/locations"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <Locations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/locations/:locationId/timezone"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <LocationTimezone />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professionals"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopStaffManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/staff"
                      element={
                        <Navigate to="/dashboard/professionals" replace />
                      }
                    />
                    <Route
                      path="/dashboard/managers"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <ShopManagers />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professionals/:staffId"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopStaffProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/staff/:staffId"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <ShopStaffProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-rentals"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <BoothRentalManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/time-off-requests"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <TimeOffRequests />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/customers"
                      element={
                        <ProtectedRoute allowedRoles={["R1", "R2"]}>
                          <Contacts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/customers/import"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <CustomerImport />
                        </ProtectedRoute>
                      }
                    />
                    {/* Service Requests - Hidden */}
                    {/* <Route
              path="/dashboard/service-requests"
              element={<ServiceRequestsManagement />}
            /> */}
                    <Route
                      path="/dashboard/shop/:shopId"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <LocationProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/shop/s/:slug"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <LocationProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/multi-location-requests"
                      element={
                        <ProtectedRoute allowedRoles={["R1"]}>
                          <MultiLocationRequests />
                        </ProtectedRoute>
                      }
                    />
                    {/* Professional Dashboard - Unified route for shop staff professionals */}
                    <Route
                      path="/dashboard/professional"
                      element={
                        <ProtectedRoute>
                          <StaffDashboard />
                        </ProtectedRoute>
                      }
                    />
                    {/* Booth Renter Dashboard - R7 role */}
                    <Route
                      path="/dashboard/booth-renter"
                      element={
                        <ProtectedRoute>
                          <BoothRenterDashboard />
                        </ProtectedRoute>
                      }
                    />
                    {/* Booth Renter Schedule - R7 uses freelancer schedule with salon constraints */}
                    <Route
                      path="/dashboard/booth-renter/schedule"
                      element={
                        <ProtectedRoute>
                          <FreelancerSchedule />
                        </ProtectedRoute>
                      }
                    />
                    {/* All other booth renter pages use freelancer components */}
                    <Route
                      path="/dashboard/booth-renter/profile"
                      element={
                        <ProtectedRoute>
                          <FreelancerProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/profile/edit"
                      element={
                        <ProtectedRoute>
                          <FreelancerProfileEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/services"
                      element={
                        <ProtectedRoute>
                          <FreelancerServices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/services/add"
                      element={
                        <ProtectedRoute>
                          <FreelancerServiceAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/services/bulk-add"
                      element={
                        <ProtectedRoute>
                          <FreelancerServiceBulkAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/services/edit/:serviceId"
                      element={
                        <ProtectedRoute>
                          <FreelancerServiceEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/bookings"
                      element={
                        <ProtectedRoute>
                          <FreelancerBookings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/bookings/:id"
                      element={
                        <ProtectedRoute>
                          <FreelancerBookingDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/bookings/import"
                      element={
                        <ProtectedRoute allowedRoles={["R7"]}>
                          <FreelancerBookingImport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/payouts"
                      element={
                        <ProtectedRoute>
                          <StaffPayouts roleType="booth-renter" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/products"
                      element={
                        <ProtectedRoute>
                          <FreelancerProducts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/products/add"
                      element={
                        <ProtectedRoute>
                          <FreelancerProductAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/products/edit/:productId"
                      element={
                        <ProtectedRoute>
                          <FreelancerProductEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/products/storefront-setup"
                      element={
                        <ProtectedRoute>
                          <StorefrontSetupPage roleType="R7" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/orders"
                      element={
                        <ProtectedRoute>
                          <FreelancerOrders />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/analytics"
                      element={
                        <ProtectedRoute>
                          <FreelancerAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/marketing"
                      element={
                        <ProtectedRoute>
                          <FreelancerMarketingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/marketing/sms-credits"
                      element={
                        <ProtectedRoute>
                          <FreelancerSMSCreditsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/reviews"
                      element={
                        <ProtectedRoute>
                          <FreelancerReviews />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/waitlist"
                      element={
                        <ProtectedRoute>
                          <WaitlistManagement roleType="booth-renter" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/notifications"
                      element={
                        <ProtectedRoute>
                          <FreelancerNotifications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/earnings"
                      element={
                        <ProtectedRoute>
                          <FreelancerEarnings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/settings"
                      element={
                        <ProtectedRoute>
                          <FreelancerSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/payment-methods"
                      element={
                        <ProtectedRoute>
                          <FreelancerPaymentMethods />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/looks"
                      element={
                        <ProtectedRoute>
                          <MyLooksPage roleType="booth-renter" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/inventory"
                      element={
                        <ProtectedRoute allowedRoles={["R7"]}>
                          <ServiceInventoryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/inventory/reports"
                      element={
                        <ProtectedRoute allowedRoles={["R7"]}>
                          <ServiceInventoryPage view="reports" />
                        </ProtectedRoute>
                      }
                    />
                    {/* HIDDEN - R7 does not pay a platform subscription; these routes are preserved for future use */}
                    {/* /dashboard/booth-renter/subscription → FreelancerSubscription (Phase 5: re-evaluate) */}
                    {/* /dashboard/booth-renter/subscription/payment → PaymentMethodSetup (Phase 5: re-evaluate) */}
                    {/* /dashboard/booth-renter/billing → rent payment history; NOT YET IMPLEMENTED (Phase 5) */}
                    <Route
                      path="/dashboard/booth-renter/customers"
                      element={
                        <ProtectedRoute>
                          <Contacts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/booth-renter/customers/import"
                      element={
                        <ProtectedRoute>
                          <CustomerImport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/profile"
                      element={
                        <ProtectedRoute>
                          <StaffProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/profile/edit"
                      element={
                        <ProtectedRoute>
                          <StaffProfileEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/services"
                      element={
                        <ProtectedRoute>
                          <StaffServices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/services/add"
                      element={
                        <ProtectedRoute>
                          <StaffServiceAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/services/bulk-add"
                      element={
                        <ProtectedRoute>
                          <StaffServiceBulkAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/services/edit/:serviceId"
                      element={
                        <ProtectedRoute>
                          <StaffServiceEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/schedule"
                      element={
                        <ProtectedRoute>
                          <UnifiedScheduleCalendar />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/bookings"
                      element={
                        <ProtectedRoute>
                          <StaffBookings />
                        </ProtectedRoute>
                      }
                    />
                    {/* Temporarily hidden - Professional customers page */}
                    {/* <Route
                path="/dashboard/professional/customers"
                element={
                  <ProtectedRoute>
                    <Contacts />
                  </ProtectedRoute>
                }
              /> */}
                    <Route
                      path="/dashboard/professional/bookings/:id"
                      element={
                        <ProtectedRoute>
                          <BookingDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/bookings/import"
                      element={
                        <ProtectedRoute allowedRoles={["R3"]}>
                          <StaffBookingImport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/reviews"
                      element={
                        <ProtectedRoute>
                          <StaffReviews />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/notifications"
                      element={
                        <ProtectedRoute>
                          <StaffNotifications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/settings"
                      element={
                        <ProtectedRoute>
                          <StaffSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/payouts"
                      element={
                        <ProtectedRoute
                          allowedRoles={["R3"]}
                          requireActiveRoleMatch={true}
                          denyDualRoleR1R3={true}
                        >
                          <StaffPayouts roleType="professional" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/looks"
                      element={
                        <ProtectedRoute>
                          <MyLooksPage roleType="professional" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/earnings"
                      element={
                        <ProtectedRoute>
                          <StaffEarningsPerformance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/analytics"
                      element={
                        <ProtectedRoute>
                          <StaffEarningsPerformance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/services"
                      element={
                        <ProtectedRoute>
                          <StaffServices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/packages"
                      element={
                        <Navigate to="/dashboard/packages" replace />
                      }
                    />
                    <Route
                      path="/dashboard/professional/package-subscriptions"
                      element={
                        <ProtectedRoute>
                          <PackageSubscriptions />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/waitlist"
                      element={
                        <ProtectedRoute>
                          <WaitlistManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/professional/multi-location"
                      element={
                        <ProtectedRoute>
                          <MultiLocationRequest />
                        </ProtectedRoute>
                      }
                    />
                    {/* Freelancer Dashboard - Independent professional route */}
                    <Route
                      path="/dashboard/freelancer"
                      element={
                        <ProtectedRoute>
                          <FreelancerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/profile"
                      element={
                        <ProtectedRoute>
                          <FreelancerProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/profile/edit"
                      element={
                        <ProtectedRoute>
                          <FreelancerProfileEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/services"
                      element={
                        <ProtectedRoute>
                          <FreelancerServices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/services/add"
                      element={
                        <ProtectedRoute>
                          <FreelancerServiceAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/services/bulk-add"
                      element={
                        <ProtectedRoute>
                          <FreelancerServiceBulkAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/services/edit/:serviceId"
                      element={
                        <ProtectedRoute>
                          <FreelancerServiceEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/schedule"
                      element={
                        <ProtectedRoute>
                          <FreelancerSchedule />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/bookings"
                      element={
                        <ProtectedRoute>
                          <FreelancerBookings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/bookings/:id"
                      element={
                        <ProtectedRoute>
                          <FreelancerBookingDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/inventory"
                      element={
                        <ProtectedRoute>
                          <ServiceInventoryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/inventory/reports"
                      element={
                        <ProtectedRoute>
                          <ServiceInventoryPage view="reports" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/products"
                      element={
                        <ProtectedRoute>
                          <FreelancerProducts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/products/add"
                      element={
                        <ProtectedRoute>
                          <FreelancerProductAdd />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/products/edit/:productId"
                      element={
                        <ProtectedRoute>
                          <FreelancerProductEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/products/storefront-setup"
                      element={
                        <ProtectedRoute>
                          <StorefrontSetupPage roleType="R4" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/orders"
                      element={
                        <ProtectedRoute>
                          <FreelancerOrders />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/analytics"
                      element={
                        <ProtectedRoute>
                          <FreelancerAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/marketing"
                      element={
                        <ProtectedRoute>
                          <FreelancerMarketingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/marketing/sms-credits"
                      element={
                        <ProtectedRoute>
                          <FreelancerSMSCreditsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/reviews"
                      element={
                        <ProtectedRoute>
                          <FreelancerReviews />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/waitlist"
                      element={
                        <ProtectedRoute>
                          <WaitlistManagement roleType="freelancer" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/notifications"
                      element={
                        <ProtectedRoute>
                          <FreelancerNotifications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/payouts"
                      element={
                        <ProtectedRoute>
                          <StaffPayouts roleType="freelancer" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/looks"
                      element={
                        <ProtectedRoute>
                          <MyLooksPage roleType="freelancer" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/settings"
                      element={
                        <ProtectedRoute>
                          <FreelancerSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/subscription"
                      element={
                        <ProtectedRoute>
                          <FreelancerSubscription />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/subscription/checkout"
                      element={
                        <ProtectedRoute>
                          <CheckoutPreview />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/subscription/payment"
                      element={
                        <ProtectedRoute>
                          <PaymentMethodSetup />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/billing"
                      element={
                        <ProtectedRoute>
                          <FreelancerBilling />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/payment-methods"
                      element={
                        <ProtectedRoute>
                          <FreelancerPaymentMethods />
                        </ProtectedRoute>
                      }
                    />
                    {/* Subscription & Pricing */}
                    <Route
                      path="/pricing"
                      element={
                        <ProtectedRoute>
                          <PricingPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Freelancer Customers */}
                    <Route
                      path="/dashboard/freelancer/customers"
                      element={
                        <ProtectedRoute>
                          <Contacts />
                        </ProtectedRoute>
                      }
                    />
                    {/* Freelancer Import Routes */}
                    <Route
                      path="/dashboard/freelancer/customers/import"
                      element={
                        <ProtectedRoute>
                          <CustomerImport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/freelancer/bookings/import"
                      element={
                        <ProtectedRoute allowedRoles={["R4"]}>
                          <FreelancerBookingImport />
                        </ProtectedRoute>
                      }
                    />
                    {/* Portal Admin Routes */}
                    <Route
                      path="/portal-admin"
                      element={
                        <ProtectedRoute>
                          <PortalDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/settings"
                      element={
                        <ProtectedRoute>
                          <PlatformSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/theme"
                      element={
                        <ProtectedRoute>
                          <ThemeSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/email-senders"
                      element={
                        <ProtectedRoute>
                          <EmailSenderSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/businesses"
                      element={
                        <ProtectedRoute>
                          <BusinessManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/businesses/:businessId"
                      element={
                        <ProtectedRoute>
                          <BusinessDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/professional-types"
                      element={
                        <ProtectedRoute>
                          <ProfessionalTypesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/business-categories"
                      element={
                        <ProtectedRoute>
                          <BusinessTypesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/product-categories"
                      element={
                        <ProtectedRoute>
                          <ProductCategoriesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/service-categories"
                      element={
                        <ProtectedRoute>
                          <ServiceCategoriesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/service-templates"
                      element={
                        <ProtectedRoute>
                          <ServiceTemplatesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/fraud"
                      element={
                        <ProtectedRoute>
                          <FraudDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/promo-codes"
                      element={
                        <ProtectedRoute>
                          <PromoCodeManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/revenue"
                      element={
                        <ProtectedRoute>
                          <RevenueAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/analytics"
                      element={
                        <ProtectedRoute>
                          <AnalyticsDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/transfer-jobs"
                      element={
                        <ProtectedRoute>
                          <TransferJobs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/payout-schedule"
                      element={
                        <ProtectedRoute>
                          <PayoutWeekdayDistribution />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/refund-governance"
                      element={
                        <ProtectedRoute>
                          <RefundGovernance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/product-returns"
                      element={
                        <ProtectedRoute>
                          <ProductReturnsOpsConsole />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/frozen-packages"
                      element={
                        <ProtectedRoute>
                          <PackageFreezeReviews />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/shop-tax-rates"
                      element={
                        <ProtectedRoute>
                          <ShopTaxRates />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/product-tax-audit"
                      element={
                        <ProtectedRoute>
                          <ProductTaxAudit />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/product-transactions"
                      element={
                        <ProtectedRoute>
                          <ProductTransactions />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/users"
                      element={
                        <ProtectedRoute>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/support-agents"
                      element={
                        <ProtectedRoute>
                          <SupportAgentsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/referral-agents"
                      element={
                        <ProtectedRoute allowedRoles={["R6"]}>
                          <ReferralAgentsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/support-migration"
                      element={
                        <ProtectedRoute allowedRoles={["R6"]}>
                          <SupportMigration />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/leads"
                      element={
                        <ProtectedRoute>
                          <LeadsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/country-waitlist"
                      element={
                        <ProtectedRoute>
                          <CountryWaitlistManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/marketing"
                      element={
                        <ProtectedRoute>
                          <MarketingDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/shops"
                      element={
                        <ProtectedRoute>
                          <ShopManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/looks"
                      element={
                        <ProtectedRoute>
                          <LooksModeration />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/portal-admin/sms-optouts"
                      element={
                        <ProtectedRoute>
                          <SMSOptOutManagement />
                        </ProtectedRoute>
                      }
                    />
                    {isDemoEnvironment && (
                      <Route
                        path="/portal-admin/demo-data-reset"
                        element={
                          <ProtectedRoute>
                            <DemoDataReset />
                          </ProtectedRoute>
                        }
                      />
                    )}
                    <Route
                      path="/complete-registration"
                      element={<CompleteRegistrationPage />}
                    />
                    {/* Login Verification Code */}
                    <Route path="/verify-code" element={<VerifyCodePage />} />
                    <Route path="/choose-role" element={<MultiRoleLanding />} />
                    </Routes>
                  </PhoneCompletionGate>
                </SessionTimeoutGuard>
              </CountryCheck>
            </Router>
          </ChecklistProvider>
        </CartProvider>
      </DemoModeProvider>
    </HelmetProvider>
  );
}

export default App;
